import { FC, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { HStack, Link, Stack, useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { formatAmount } from 'common/formatAmount';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import Skeleton from 'components/Skeleton';
import useToast from 'components/ToastProvider/useToast';
import { formatPeriodDate } from 'features/Accounting/pages/Estimation/common/utils';
import TaxStatus from 'features/Accounting/pages/Estimation/components/TaxStatus';
import { UpdateTaxDataDocument } from 'features/Accounting/pages/Estimation/graphql/updateTaxData.gql';

import StepLayout from '../../layout/Step';
import { StepComponentProps } from '../types';
import AdditionalInfo from './components/AdditionalInfo';
import MixedActivityInfo from './components/MixedActivityInfo';
import { useTaxForecastPeriodRevenueQueryFilters } from './hooks/useTaxForecastPeriodRevenueQueryFilters';
import { useTaxForecastRevenue } from './hooks/useTaxForecastRevenue';
import { locales } from './locales';
import {
  RevenueSituationSchema,
  revenueSituationSchema,
} from './revenueSituation.schema';

const VAT_INFO_ARTICLE_URL =
  'https://www.shine.fr/blog/assujetti-tva-auto-entrepreneur/';

type SavePayload = {
  bicDistributionAmount: number | null;
  bncDistributionAmount: number | null;
  complementaryRevenue: number;
  sellingDistributionAmount: number | null;
  vatAmount: number | null;
};

interface RevenueSituationProps extends StepComponentProps<SavePayload> {
  onRevenueFetched: (revenue: number) => void;
}

const RevenueSituation: FC<RevenueSituationProps> = ({
  context,
  onBack,
  onNext,
  onRevenueFetched,
  onSave,
}) => {
  const { showToast } = useToast();
  const { stepProgress, taxForecastPeriod, taxForecastProfile } = context;

  const {
    isOpen: isModalOpen,
    onClose: onModalClose,
    onOpen: onModalOpen,
  } = useDisclosure();
  const {
    isOpen: isSidebarOpen,
    onClose: onCloseSidebar,
    onOpen: onOpenSidebar,
  } = useDisclosure();

  const filters = useTaxForecastPeriodRevenueQueryFilters({
    // @ts-expect-error Need to fix typing, taxForecastPeriod is always defined on this step
    transactionAfter: taxForecastPeriod?.periodStartsAt,
    // @ts-expect-error Need to fix typing, taxForecastPeriod is always defined on this step
    transactionBefore: taxForecastPeriod?.periodEndsAt,
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<RevenueSituationSchema>({
    defaultValues: {
      bicAmount: taxForecastPeriod?.bicDistributionAmount,
      bncAmount: taxForecastPeriod?.bncDistributionAmount,
      complementaryRevenue: taxForecastPeriod?.complementaryRevenue,
      isMixedActivityChecked: Boolean(
        taxForecastPeriod?.bicDistributionAmount ||
          taxForecastPeriod?.bncDistributionAmount ||
          taxForecastPeriod?.sellingDistributionAmount,
      ),
      revenue: taxForecastPeriod?.revenue ?? 0,
      sellingAmount: taxForecastPeriod?.sellingDistributionAmount,
      vatAmount: taxForecastPeriod?.vatAmount,
    },
    resolver: zodResolver(revenueSituationSchema),
  });

  const { loading: transactionsSumLoading } = useTaxForecastRevenue({
    companyProfileId: context.companyProfile.companyProfileId,
    filters,
    onCompleted: ({ transactionsSum }) => {
      setValue('revenue', transactionsSum);
      onRevenueFetched(transactionsSum);
    },
  });

  const [updateTaxData, { loading: updateTaxDataLoading }] = useMutation(
    UpdateTaxDataDocument,
    { onError: () => showToast({ message: locales.error, type: 'error' }) },
  );

  const [revenue, complementaryRevenue, vatAmount] = watch([
    'revenue',
    'complementaryRevenue',
    'vatAmount',
  ]);

  /**
   * We can't use the computedRevenue field here as we're fetching the current revenue separately in this component
   * and it might be different than what was used in the last tax computation
   */
  const totalFormatted = useMemo<string>(() => {
    const total = revenue + (complementaryRevenue || 0) - (vatAmount || 0);

    return formatAmount({ forcePlusSign: false, value: total });
  }, [revenue, complementaryRevenue, vatAmount]);

  const isSubjectToVat = Boolean(taxForecastProfile?.isSubjectToVat);

  const onSubmit: SubmitHandler<RevenueSituationSchema> = async (data) => {
    const input: SavePayload = {
      bicDistributionAmount: null,
      bncDistributionAmount: null,
      complementaryRevenue: data.complementaryRevenue || 0,
      sellingDistributionAmount: null,
      vatAmount: null,
    };

    if (data.isMixedActivityChecked) {
      input.bicDistributionAmount = data.bicAmount;
      input.bncDistributionAmount = data.bncAmount;
      input.sellingDistributionAmount = data.sellingAmount;
    }

    if (isSubjectToVat) {
      input.vatAmount = data.vatAmount || 0;
    }

    await updateTaxData({
      onCompleted: () => {
        onSave(input);
        onNext();
      },
      variables: {
        input: {
          companyProfileId: context.companyProfile.companyProfileId,
          taxForecastPeriodData: {
            activityDistributionAmounts: {
              bicDistributionAmount: input.bicDistributionAmount,
              bncDistributionAmount: input.bncDistributionAmount,
              sellingDistributionAmount: input.sellingDistributionAmount,
            },
            complementaryRevenue: input.complementaryRevenue,
            vatAmount: input.vatAmount,
          },
          taxFrequency: context.companyProfile.taxFrequency,
        },
      },
    });
  };

  return (
    <StepLayout>
      <StepLayout.Header
        onBack={onBack}
        onOpenDrawer={() => onOpenSidebar()}
        stepProgress={stepProgress}
      />

      <StepLayout.Body>
        <Stack>
          <Heading size="lg">{locales.title}</Heading>

          <Text size="lg">{locales.subtitle}</Text>
        </Stack>

        <HStack align="center" gap="space-8" marginY="space-32">
          <Text fontWeight="weight-400" variant="primary">
            {formatPeriodDate(
              taxForecastPeriod?.periodStartsAt ?? 0,
              taxForecastPeriod?.periodEndsAt ?? 0,
            )}
          </Text>

          <TaxStatus
            isPaid={taxForecastPeriod?.isPaid ?? false}
            payBefore={taxForecastPeriod?.payBefore ?? 0}
          />
        </HStack>

        <Text size="sm" variant="light">
          {locales.revenue}
        </Text>

        <Skeleton isLoaded={!transactionsSumLoading}>
          <Heading display="inline" size="xl" variant="number">
            {totalFormatted}
          </Heading>

          {isSubjectToVat ? (
            <Heading
              display="inline"
              paddingLeft="space-4"
              size="sm"
              variant="number"
            >
              {locales.withoutVat}
            </Heading>
          ) : null}
        </Skeleton>

        {complementaryRevenue && !isSubjectToVat ? (
          <Text variant="light">
            {locales.formatString(locales.complementaryRevenue, {
              amount: formatAmount({
                forcePlusSign: false,
                value: complementaryRevenue || 0,
              }),
            })}
          </Text>
        ) : null}

        {isSubjectToVat ? (
          <Text variant="light">
            {locales.formatString(locales.complementaryRevenueWithVAT, {
              complementaryRevenue: formatAmount({
                forcePlusSign: false,
                value: complementaryRevenue || 0,
              }),
              vatAmount: formatAmount({
                forcePlusSign: false,
                value: vatAmount || 0,
              }),
            })}
          </Text>
        ) : null}

        <Callout marginTop="space-32" variant="info" width="fit-content">
          {locales.callout}
        </Callout>

        <Form.Field label={locales.form.label.revenue} marginTop="space-16">
          <Skeleton isLoaded={!transactionsSumLoading}>
            <Form.Input
              {...register('revenue', { valueAsNumber: true })}
              isDisabled={true}
              rightIcon="euro"
              type="number"
            />
          </Skeleton>
        </Form.Field>

        <Form.Field
          error={errors.complementaryRevenue?.message}
          label={locales.form.label.complementaryRevenue}
          marginTop="space-16"
        >
          <Form.Input
            {...register('complementaryRevenue', {
              deps: ['bicAmount', 'bncAmount', 'sellingAmount'],
              valueAsNumber: true,
            })}
            inputMode="decimal"
            onFocus={(e) => e.target.select()}
            placeholder="0"
            rightIcon="euro"
            type="number"
          />
        </Form.Field>

        {isSubjectToVat ? (
          <>
            <Form.Field
              error={errors.vatAmount?.message}
              label={locales.form.label.vat}
              marginTop="space-16"
            >
              <Form.Input
                {...register('vatAmount', {
                  deps: ['bicAmount', 'bncAmount', 'sellingAmount'],
                  valueAsNumber: true,
                })}
                inputMode="decimal"
                onFocus={(e) => e.target.select()}
                placeholder="0"
                rightIcon="euro"
                type="number"
              />
            </Form.Field>

            <Link
              color="grey.600"
              fontSize="font-12"
              fontWeight="weight-400"
              href={VAT_INFO_ARTICLE_URL}
              isExternal={true}
              paddingTop="space-4"
            >
              {locales.form.label.vatInfo}
            </Link>
          </>
        ) : null}

        <Controller
          control={control}
          name="isMixedActivityChecked"
          render={({ field }) => (
            <SunshineCard.Group marginTop="space-32">
              <SunshineCard
                isChecked={field.value}
                onChange={() => field.onChange(!field.value)}
                title={locales.mixActivity}
                variant="checkbox"
              />

              {field.value ? (
                <SunshineCard>
                  <SunshineCard.Content alignItems="stretch" gap="space-16">
                    <Form.Field
                      error={errors?.bicAmount?.message}
                      label={
                        isSubjectToVat
                          ? locales.form.label.bicWithVAT
                          : locales.form.label.bic
                      }
                    >
                      <Form.Input
                        {...register('bicAmount', {
                          deps: [
                            'complementaryRevenue',
                            'bncAmount',
                            'sellingAmount',
                          ],
                          valueAsNumber: true,
                        })}
                        inputMode="decimal"
                        onFocus={(e) => e.target.select()}
                        placeholder="0"
                        rightIcon="euro"
                        type="number"
                      />
                    </Form.Field>

                    <Form.Field
                      error={errors?.bncAmount?.message}
                      label={
                        isSubjectToVat
                          ? locales.form.label.bncWithVat
                          : locales.form.label.bnc
                      }
                    >
                      <Form.Input
                        {...register('bncAmount', {
                          deps: [
                            'complementaryRevenue',
                            'bicAmount',
                            'sellingAmount',
                          ],
                          valueAsNumber: true,
                        })}
                        inputMode="decimal"
                        onFocus={(e) => e.target.select()}
                        placeholder="0"
                        rightIcon="euro"
                        type="number"
                      />
                    </Form.Field>

                    <Form.Field
                      error={errors?.sellingAmount?.message}
                      label={
                        isSubjectToVat
                          ? locales.form.label.sellingWithVAT
                          : locales.form.label.selling
                      }
                    >
                      <Form.Input
                        {...register('sellingAmount', {
                          deps: [
                            'complementaryRevenue',
                            'bicAmount',
                            'bncAmount',
                          ],
                          valueAsNumber: true,
                        })}
                        inputMode="decimal"
                        onFocus={(e) => e.target.select()}
                        placeholder="0"
                        rightIcon="euro"
                        type="number"
                      />
                    </Form.Field>

                    <Button
                      alignSelf="flex-start"
                      icon="link"
                      onClick={() => onModalOpen()}
                      variant="inline-secondary"
                    >
                      {locales.more}
                    </Button>
                  </SunshineCard.Content>
                </SunshineCard>
              ) : null}
            </SunshineCard.Group>
          )}
        />
      </StepLayout.Body>

      {/** This is rendered as a Drawer when in webView */}
      <AdditionalInfo isOpen={isSidebarOpen} onClose={onCloseSidebar} />

      <StepLayout.Footer stepProgress={stepProgress}>
        <StepLayout.FooterButton
          isDisabled={transactionsSumLoading}
          isLoading={updateTaxDataLoading}
          onClick={handleSubmit(onSubmit)}
        />
      </StepLayout.Footer>

      {/** This is rendered as a Drawer when in webView */}
      <MixedActivityInfo isOpen={isModalOpen} onClose={onModalClose} />
    </StepLayout>
  );
};

export default RevenueSituation;
